import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import type { SanityPageBuilderMediaSliderSection, SanityPageBuilderSection } from "@ouster/shared";
import { up } from "~/features/app/screens";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Icon } from "~/features/ui/icon";
import { type Shape, Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { Wrap } from "~/features/ui/wrap";
import CaretLeft from "~/public/icons/caret-left.svg";
import CaretRight from "~/public/icons/caret-right.svg";
import { hasRenderableSanityMedia, SanityMediaFigure } from "./media-figure";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderMediaSliderSection;
  };
};
export function SanityMediaSliderSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const items = sectionContent.items?.filter(item => hasRenderableSanityMedia(item.media)) ?? [];
  const isLg = useMediaQuery(up("lg"));
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [lastSlideIndex, setLastSlideIndex] = React.useState(0);
  const sliderRef = React.useRef<Shape>(null);
  React.useEffect(() => {
    sliderRef.current?.splide?.refresh();
  }, [isLg]);
  if (!items.length) {
    return null;
  }
  const mode = sectionSettings?.mode || "light";
  const theme = sectionSettings?.theme || "white";
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  return <_StyledSection data-theme={mode} id={sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key} data-sanity-section-key={section._key} data-sanity-section-type={section._type} data-anchor-name={sectionSettings?.anchorName} $_css={[{
    "position": "relative",
    "color": "var(--text-primary)"
  },
  // v3: white theme renders true white (bg-primary is mode-aware); grey untouched.
  theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, sectionSettings?.hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" && mode === "dark" && {
    "backgroundColor": "var(--bg-primary)"
  }]}>
      <_StyledWrap>
        <_StyledDiv>
          <Slider ref={sliderRef} onMounted={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} onMove={splide => {
          setActiveSlideIndex(splide.Components.Controller.getIndex());
          setLastSlideIndex(splide.Components.Controller.getEnd());
        }} onResize={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} options={{
          fixedWidth: "90%",
          autoWidth: false,
          padding: 0,
          mediaQuery: "min",
          breakpoints: {
            1024: {
              fixedWidth: undefined,
              padding: 40,
              autoWidth: true
            }
          }
        }}>
            {items.map((item, index) => <_StyledSliderSlide key={item._key || index} $_css2={[{
            "paddingLeft": "var(--spacing-md)",
            "paddingRight": "var(--spacing-md)",
            "paddingTop": "var(--spacing-md)",
            "&:not(:last-child)": {
              "paddingRight": "0"
            },
            "@media (min-width: 1024px)": {
              "paddingLeft": "var(--spacing-md)",
              "paddingRight": "var(--spacing-md)",
              "paddingTop": "var(--spacing-xl)",
              "&:first-child": {
                "paddingLeft": "0"
              },
              "&:last-child": {
                "paddingRight": "0"
              }
            }
          }]}>
                <SanityMediaFigure media={item.media} ratio={isLg ? undefined : "5/6"} height={isLg ? 520 : undefined} sizes="lg:50vw" />
              </_StyledSliderSlide>)}
          </Slider>

          {items.length > 1 && <_StyledDiv2 $_css3={[{
          "display": "flex",
          "alignItems": "center",
          "gap": "var(--spacing-lg)",
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)",
          "@media (min-width: 1024px)": {
            "padding": "var(--spacing-xl)"
          }
        }, themeStyle.border]}>
              <_StyledDiv3>
                <Progress activeSlideIndex={activeSlideIndex} totalSlides={lastSlideIndex + 1} />
              </_StyledDiv3>
              <_StyledDiv4>
                <_StyledButton type="button" onClick={() => sliderRef.current?.go("<")} disabled={activeSlideIndex === 0} $_css4={[{
              "display": "flex",
              "height": "3.2rem",
              "width": "3.2rem",
              "alignItems": "center",
              "justifyContent": "center",
              "borderRadius": "0",
              "borderBottomLeftRadius": "4px",
              "borderTopLeftRadius": "4px",
              "backgroundColor": "var(--bg-transparent-20) !important",
              "color": "var(--text-primary)",
              "transitionProperty": "opacity"
            }]}>
                  <_StyledIcon icon={CaretLeft} size="16" $_css5={[activeSlideIndex === 0 && {
                "opacity": "0.3"
              }]} />
                </_StyledButton>
                <_StyledButton2 type="button" onClick={() => sliderRef.current?.go(">")} disabled={activeSlideIndex >= lastSlideIndex} $_css6={[{
              "display": "flex",
              "height": "3.2rem",
              "width": "3.2rem",
              "alignItems": "center",
              "justifyContent": "center",
              "borderRadius": "0",
              "borderBottomRightRadius": "4px",
              "borderTopRightRadius": "4px",
              "backgroundColor": "var(--bg-transparent-20) !important",
              "color": "var(--text-primary)",
              "transitionProperty": "opacity"
            }]}>
                  <_StyledIcon2 icon={CaretRight} size="16" $_css7={[activeSlideIndex >= lastSlideIndex && {
                "opacity": "0.3"
              }]} />
                </_StyledButton2>
              </_StyledDiv4>
            </_StyledDiv2>}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-yajbfy-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-yajbfy-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-yajbfy-2"
})({
  "position": "relative",
  "zIndex": "10"
});
var _StyledSliderSlide = _styled4(Slider.Slide).withConfig({
  componentId: "sc-sf-yajbfy-3"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-yajbfy-4"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-yajbfy-5"
})({
  "flex": "1 1 0%"
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-yajbfy-6"
})({
  "display": "flex"
});
var _StyledButton = _styled8("button").withConfig({
  componentId: "sc-sf-yajbfy-7"
})`${p => p.$_css4}`;
var _StyledIcon = _styled9(Icon).withConfig({
  componentId: "sc-sf-yajbfy-8"
})`${p => p.$_css5}`;
var _StyledButton2 = _styled10("button").withConfig({
  componentId: "sc-sf-yajbfy-9"
})`${p => p.$_css6}`;
var _StyledIcon2 = /*#__PURE__*/_styled11(Icon).withConfig({
  displayName: "media-slider___StyledIcon2",
  componentId: "sc-183d8c7-0"
})(["", ""], p => p.$_css7);